﻿import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, CalendarOff, Umbrella } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { leaveService } from '@/services/leave.service';

interface LeaveWidgetProps {
  isAdminTier: boolean;
  isManagerTier: boolean;
}

type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | string;

function leaveStatusVariant(status: LeaveStatus): 'warning' | 'success' | 'destructive' | 'outline' | 'secondary' {
  if (status === 'pending') return 'warning';
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'destructive';
  if (status === 'cancelled') return 'outline';
  return 'secondary';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function SkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />
      ))}
    </div>
  );
}

function AdminLeave() {
  const { has } = usePermission();

  const pendingQ = useQuery({
    queryKey: ['dashboard', 'leave', 'team-pending'],
    queryFn: () => leaveService.listTeamRequests({ status: 'pending', page: 1, limit: 5 }),
    enabled: has(PERMISSIONS.LEAVE_APPROVE),
  });

  const items = pendingQ.data?.data ?? [];
  const total = pendingQ.data?.pagination?.total ?? 0;

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<CalendarOff />}
        title="Pending Leave Requests"
        action={
          <Link to="/hrms/leave" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="p-0">
        {pendingQ.isLoading ? (
          <SkeletonRows />
        ) : pendingQ.isError ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-muted-foreground">Failed to load</p>
            <Button size="sm" variant="outline" onClick={() => pendingQ.refetch()}>Retry</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <CalendarOff className="h-8 w-8 opacity-30" />
            <p className="text-sm text-muted-foreground">No pending leave requests</p>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {items.map((req) => (
              <div key={req.id} className="flex items-start justify-between gap-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {req.target_first_name} {req.target_last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {req.leave_type_name} {formatDate(req.start_date)}
                    {req.end_date !== req.start_date ? ` â†’ ${formatDate(req.end_date)}` : ''}
                  </p>
                </div>
                <Badge variant="warning">Pending</Badge>
              </div>
            ))}
            {total > 5 && (
              <div className="px-4 py-2 text-center">
                <Link to="/hrms/leave" className="text-xs text-primary hover:underline">
                  +{total - 5} more
                </Link>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UserLeave() {
  const { user } = useAuth();

  const balanceQ = useQuery({
    queryKey: ['dashboard', 'leave', 'balance', user?.id],
    queryFn: () => leaveService.getMyBalances(),
    enabled: !!user?.id,
  });

  const recentQ = useQuery({
    queryKey: ['dashboard', 'leave', 'my-recent'],
    queryFn: () => leaveService.listMyRequests({ page: 1, limit: 3 }),
  });

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<Umbrella />}
        title="My Leave"
        action={
          <Link to="/leave" className="text-xs font-normal text-primary hover:underline">
            Apply Leave 
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        {balanceQ.isLoading ? (
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted/50" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {(balanceQ.data ?? []).slice(0, 3).map((b) => (
              <div
                key={b.leave_type_id}
                className="rounded-lg border bg-background/60 p-2 text-center"
              >
                <p className="text-lg font-bold text-teal-700">{b.available ?? 0}</p>
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground" title={b.leave_type_name}>
                  {b.leave_type_name}
                </p>
              </div>
            ))}
            {(balanceQ.data ?? []).length === 0 && (
              <p className="col-span-3 text-center text-sm text-muted-foreground">No leave types configured</p>
            )}
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recent Requests
          </p>
          {recentQ.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-lg bg-muted/50" />
              ))}
            </div>
          ) : (recentQ.data?.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent leave requests</p>
          ) : (
            <div className="space-y-2">
              {(recentQ.data?.data ?? []).map((req) => (
                <div key={req.id} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {formatDate(req.start_date)}
                    {req.end_date !== req.start_date ? ` â†’ ${formatDate(req.end_date)}` : ''} {req.leave_type_name}
                  </span>
                  <Badge variant={leaveStatusVariant(req.status)}>{req.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function LeaveWidget({ isAdminTier, isManagerTier }: LeaveWidgetProps) {
  if (isAdminTier || isManagerTier) return <AdminLeave />;
  return <UserLeave />;
}
